// Display helpers for menu_drinks.category. Per fixtures/real-menu.seed.ts's
// module header, this column is "display taxonomy" for the real 73-recipe
// menu: every seeded recipe is a mixed drink, so the value is the drink's
// classified base spirit (bourbon, rye, scotch, vodka, gin, tequila, rum),
// or liqueur / non_alcoholic / other for the recipes that fall outside
// the seven-category liquor-selection vocabulary. There is no separate
// "Cocktails / Draft Beer / Wine" section grouping anywhere in the real
// seed data (design/mockups/ring.html's beer and wine sections were
// illustrative placeholders for an invented menu, not the shape of the
// real one); the menu grid groups by this same category value, which is
// the only display taxonomy the API actually returns.

export const CATEGORY_LABELS: Readonly<Record<string, string>> = {
  bourbon: "Bourbon",
  rye: "Rye",
  scotch: "Scotch",
  vodka: "Vodka",
  gin: "Gin",
  tequila: "Tequila",
  rum: "Rum",
  liqueur: "Liqueur",
  other: "Other",
  non_alcoholic: "Non-Alcoholic",
};

// Fixed presentation order: the seven liquor-slot categories in the order
// design/mockups/liquor-sheet.html already uses for its own grouped list
// (Bourbon, Rye, Scotch, ...), then the three non-selectable categories,
// non_alcoholic last since it is the category furthest from "spirit
// forward" and a bartender scanning left to right expects the lightest
// options at the end, matching ring.html's own section order (Cocktails,
// then progressively lighter categories).
export const CATEGORY_DISPLAY_ORDER: readonly string[] = [
  "bourbon",
  "rye",
  "scotch",
  "vodka",
  "gin",
  "tequila",
  "rum",
  "liqueur",
  "other",
  "non_alcoholic",
];

export function categoryLabel(category: string): string {
  const known = CATEGORY_LABELS[category];
  if (known !== undefined) return known;
  // UX105: a tenant-added category ("wine", "beer") is capitalized once, like the built-in ones, never shown lowercase.
  const spaced = category.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

// ALPHA-11: the manager console's own copy of src/domain/catalog.ts's
// LIQUOR_SLOT_CATEGORIES, for the Bottles add/edit form's category picker
// and the Drinks add/edit form's allowed-liquor-categories chip picker.
// Kept in sync by hand with the server-side list and its own database
// CHECK constraint, the same "TypeScript list plus a matching SQL CHECK"
// shape that list's own comment documents; a mismatch here would surface
// immediately as a 400 from the server on save, never a silent acceptance
// of an invalid category.
export const LIQUOR_SLOT_CATEGORIES = [
  "bourbon",
  "rye",
  "scotch",
  "vodka",
  "gin",
  "tequila",
  "rum",
] as const;

// Inventory track (track/inventory) Slice 3, Amendment 2026-08-22
// (twelfth). Display labels for src/domain/catalog.ts's ITEM_KINDS and
// CONTAINER_TYPES, the same "fixed vocabulary, human label" shape
// CATEGORY_LABELS already uses.
// ITEM1 Slice 2. This static map stops being the source of truth for a
// kind's label (ui/src/admin.ts's resolveItemKindLabel reads the tenant's
// own adminVocabularies["item_kind"] row first); it survives as the
// fallback for the window before that vocabulary has loaded, matching
// every other tenant-vocabulary fallback constant in this module's own
// admin.ts counterpart. 'cider' is included so that fallback window shows
// the right word rather than the raw key, even though this map is no
// longer authoritative.
export const ITEM_KIND_LABELS: Readonly<Record<string, string>> = {
  liquor: "Liquor",
  beer: "Beer",
  wine: "Wine",
  cider: "Cider",
  packaged: "Packaged",
};

export function itemKindLabel(itemKind: string): string {
  return ITEM_KIND_LABELS[itemKind] ?? itemKind;
}

export const CONTAINER_TYPE_LABELS: Readonly<Record<string, string>> = {
  bottle: "Bottle",
  can: "Can",
  keg: "Keg",
  other: "Other",
};

export function containerTypeLabel(containerType: string): string {
  return CONTAINER_TYPE_LABELS[containerType] ?? containerType;
}

// Amendment 2026-08-22 (thirteenth). Display labels for
// src/domain/tenantSettings.ts's PRICING_MODELS.
export const PRICING_MODEL_LABELS: Readonly<Record<string, string>> = {
  per_ounce: "Per ounce",
  shelf: "Shelf",
};

export function pricingModelLabel(pricingModel: string): string {
  return PRICING_MODEL_LABELS[pricingModel] ?? pricingModel;
}

// UX41 (docs/build-briefs/UX41-recipe-quantities-two-decimals.md, Section
// 1.2, DEC-193, DEC-197). menu_drink_components.quantity is a
// NUMERIC(12, 4) string ("1.0000"); the server's own precision is
// unchanged by this function, which is display-only and never fed back
// into pricing or inventory submission. Two decimals apply uniformly,
// including a whole number ("1.00", never "1") and the unit: "each" case
// ("2.00 each", never "2 each") -- DEC-193's own "2 decimel points, not
// 4" read literally, with DEC-197 confirming no unit-conditional
// exception. Falls back to the raw string on anything Number() cannot
// parse; no real seeded or stage0 recipe quantity is expected to hit
// that path (checked in the UX41 brief), but a display formatter must
// not throw or render garbage on a value it did not create.
export function formatRecipeQuantity(quantity: string): string {
  const parsed = Number(quantity);
  return Number.isFinite(parsed) ? parsed.toFixed(2) : quantity;
}

/** Sorts a set of category values that are actually present in the
 * fetched menu into CATEGORY_DISPLAY_ORDER, appending any unrecognized
 * value (a future category the API adds that this file has not been
 * updated for) alphabetically at the end rather than silently dropping
 * it. */
export function sortCategoriesForDisplay(categories: readonly string[]): string[] {
  const known = categories.filter((category) => CATEGORY_DISPLAY_ORDER.includes(category));
  const unknown = categories
    .filter((category) => !CATEGORY_DISPLAY_ORDER.includes(category))
    .sort((a, b) => a.localeCompare(b));
  known.sort((a, b) => CATEGORY_DISPLAY_ORDER.indexOf(a) - CATEGORY_DISPLAY_ORDER.indexOf(b));
  return [...known, ...unknown];
}
